import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Upload,
  FileText,
  Brain,
  ArrowRight,
  Check,
  X,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

// Helper to extract error message from various error formats
const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors format
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

const BankImport = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Map, 3: Review, 4: Complete
  
  // File state
  const [fileData, setFileData] = useState(null);
  const [fileType, setFileType] = useState(null); // 'csv' or 'pdf'
  const [headers, setHeaders] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  
  // Mapping state
  const [mapping, setMapping] = useState({
    account_id: '',
    date_column: '',
    description_column: '',
    amount_column: '',
    debit_column: '',
    credit_column: '',
    reference_column: '',
    date_format: '%Y-%m-%d',
    auto_categorize: true
  });
  
  // Parsed transactions
  const [transactions, setTransactions] = useState([]);
  const [pdfTransactions, setPdfTransactions] = useState([]); // Pre-parsed from PDF
  const [errors, setErrors] = useState([]);
  const [selectedTxns, setSelectedTxns] = useState(new Set());

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const res = await api.get('/accounts');
      // Filter to bank/cash accounts
      const bankAccounts = res.data.filter(a => 
        ['bank', 'cash', 'mobile_money'].includes(a.account_subtype)
      );
      setAccounts(bankAccounts);
    } catch (error) {
      toast.error('Failed to load accounts');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.csv') && !fileName.endsWith('.pdf')) {
      toast.error('Please upload a CSV or PDF file');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/bank-import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFileData(file);
      setFileType(res.data.file_type);
      setHeaders(res.data.headers);
      setSampleRows(res.data.sample_rows);
      
      // For PDF files, transactions are pre-parsed
      if (res.data.file_type === 'pdf' && res.data.transactions) {
        setPdfTransactions(res.data.transactions);
        toast.success(`PDF processed: ${res.data.row_count} transactions found (AI confidence: ${Math.round((res.data.confidence || 0) * 100)}%)`);
      } else {
        toast.success(`File loaded: ${res.data.row_count} rows found`);
      }
      
      setStep(2);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to parse file'));
    } finally {
      setLoading(false);
    }
  };

  const handleParseTransactions = async () => {
    // For PDF files, we already have parsed transactions
    if (fileType === 'pdf') {
      if (!mapping.account_id) {
        toast.error('Please select a bank account');
        return;
      }
      
      // Use pre-parsed PDF transactions
      const formattedTransactions = pdfTransactions.map(t => ({
        date: t.date,
        description: t.description,
        amount: Math.abs(t.amount),
        transaction_type: t.transaction_type,
        reference: t.reference,
        suggested_category_id: null,
        suggested_category_name: null,
        confidence: 0
      }));
      
      setTransactions(formattedTransactions);
      setSelectedTxns(new Set(formattedTransactions.map((_, i) => i)));
      setStep(3);
      toast.success(`${formattedTransactions.length} transactions ready for import`);
      return;
    }
    
    // For CSV files, parse with column mapping
    if (!mapping.account_id || !mapping.date_column || !mapping.description_column) {
      toast.error('Please select required columns');
      return;
    }
    
    if (!mapping.amount_column && !(mapping.debit_column && mapping.credit_column)) {
      toast.error('Please select amount column or both debit/credit columns');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', fileData);
      
      const params = new URLSearchParams({
        account_id: mapping.account_id,
        date_column: mapping.date_column,
        description_column: mapping.description_column,
        date_format: mapping.date_format,
        auto_categorize: mapping.auto_categorize.toString()
      });
      
      if (mapping.amount_column) {
        params.append('amount_column', mapping.amount_column);
      }
      if (mapping.debit_column) {
        params.append('debit_column', mapping.debit_column);
      }
      if (mapping.credit_column) {
        params.append('credit_column', mapping.credit_column);
      }
      if (mapping.reference_column) {
        params.append('reference_column', mapping.reference_column);
      }
      
      const res = await api.post(`/bank-import/parse?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setTransactions(res.data.transactions);
      setErrors(res.data.errors);
      // Select all by default
      setSelectedTxns(new Set(res.data.transactions.map((_, i) => i)));
      setStep(3);
      
      if (res.data.error_count > 0) {
        toast.warning(`${res.data.error_count} rows had errors`);
      } else {
        toast.success(`Parsed ${res.data.total_count} transactions`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to parse transactions'));
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    const selected = transactions.filter((_, i) => selectedTxns.has(i));
    if (selected.length === 0) {
      toast.error('Please select at least one transaction');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/bank-import/create', {
        account_id: mapping.account_id,
        transactions: selected
      });
      
      toast.success(`Imported ${res.data.created_count} transactions`);
      setStep(4);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to import transactions'));
    } finally {
      setLoading(false);
    }
  };

  const toggleTransaction = (index) => {
    const newSelected = new Set(selectedTxns);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedTxns(newSelected);
  };

  const toggleAll = () => {
    if (selectedTxns.size === transactions.length) {
      setSelectedTxns(new Set());
    } else {
      setSelectedTxns(new Set(transactions.map((_, i) => i)));
    }
  };

  const resetImport = () => {
    setStep(1);
    setFileData(null);
    setFileType(null);
    setHeaders([]);
    setSampleRows([]);
    setTransactions([]);
    setPdfTransactions([]);
    setErrors([]);
    setSelectedTxns(new Set());
    setMapping({
      account_id: '',
      date_column: '',
      description_column: '',
      amount_column: '',
      debit_column: '',
      credit_column: '',
      reference_column: '',
      date_format: '%Y-%m-%d',
      auto_categorize: true
    });
  };

  return (
    <div className="space-y-6" data-testid="bank-import-page">
      <div>
        <h1 className="text-2xl font-bold text-[hsl(var(--ink-1))]" style={{ fontFamily: 'Space Grotesk' }}>
          Import Bank Statement
        </h1>
        <p className="text-[hsl(var(--muted-ink))]">
          Import transactions from your bank statement (CSV or PDF)
        </p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= s 
                ? 'bg-[hsl(var(--brand-500))] text-white' 
                : 'bg-gray-200 text-gray-500'
            }`}>
              {step > s ? <Check size={16} /> : s}
            </div>
            {s < 4 && (
              <div className={`w-12 h-1 mx-2 ${step > s ? 'bg-[hsl(var(--brand-500))]' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-12 text-sm text-[hsl(var(--muted-ink))]">
        <span>Upload</span>
        <span>Map Columns</span>
        <span>Review</span>
        <span>Complete</span>
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload size={20} />
              Upload CSV File
            </CardTitle>
            <CardDescription>
              Select a CSV or PDF file exported from your bank
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium mb-2">Drop your bank statement here</p>
              <p className="text-sm text-[hsl(var(--muted-ink))] mb-4">Supports CSV and PDF files</p>
              <Input
                type="file"
                accept=".csv,.pdf"
                onChange={handleFileUpload}
                className="max-w-xs mx-auto"
                data-testid="bank-file-input"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Map Columns */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText size={20} />
              {fileType === 'pdf' ? 'Review Extracted Data' : 'Map Columns'}
            </CardTitle>
            <CardDescription>
              {fileType === 'pdf' 
                ? 'AI has extracted transactions from your PDF. Select a bank account and review the data.'
                : 'Match your CSV columns to transaction fields'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Account Selection */}
            <div>
              <Label>Bank Account *</Label>
              <Select value={mapping.account_id} onValueChange={(v) => setMapping({...mapping, account_id: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Column Mappings - Only for CSV */}
            {fileType !== 'pdf' && (
            <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date Column *</Label>
                <Select value={mapping.date_column} onValueChange={(v) => setMapping({...mapping, date_column: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    {headers.map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date Format</Label>
                <Select value={mapping.date_format} onValueChange={(v) => setMapping({...mapping, date_format: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="%Y-%m-%d">YYYY-MM-DD</SelectItem>
                    <SelectItem value="%m/%d/%Y">MM/DD/YYYY</SelectItem>
                    <SelectItem value="%d/%m/%Y">DD/MM/YYYY</SelectItem>
                    <SelectItem value="%Y/%m/%d">YYYY/MM/DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description Column *</Label>
                <Select value={mapping.description_column} onValueChange={(v) => setMapping({...mapping, description_column: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    {headers.map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount Column (single)</Label>
                <Select value={mapping.amount_column || '_none'} onValueChange={(v) => setMapping({...mapping, amount_column: v === '_none' ? '' : v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">None</SelectItem>
                    {headers.map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Debit Column (separate)</Label>
                <Select value={mapping.debit_column || '_none'} onValueChange={(v) => setMapping({...mapping, debit_column: v === '_none' ? '' : v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">None</SelectItem>
                    {headers.map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Credit Column (separate)</Label>
                <Select value={mapping.credit_column || '_none'} onValueChange={(v) => setMapping({...mapping, credit_column: v === '_none' ? '' : v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">None</SelectItem>
                    {headers.map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference Column (optional)</Label>
                <Select value={mapping.reference_column || '_none'} onValueChange={(v) => setMapping({...mapping, reference_column: v === '_none' ? '' : v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">None</SelectItem>
                    {headers.map(h => (
                      <SelectItem key={h} value={h}>{h}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* AI Categorization */}
            <div className="flex items-center gap-2">
              <Checkbox
                checked={mapping.auto_categorize}
                onCheckedChange={(checked) => setMapping({...mapping, auto_categorize: !!checked})}
              />
              <Label className="flex items-center gap-2">
                <Brain size={16} className="text-[hsl(var(--brand-500))]" />
                Auto-categorize with AI
              </Label>
            </div>
            </>
            )}

            {/* Sample Data Preview */}
            {sampleRows.length > 0 && (
              <div>
                <Label className="mb-2 block">
                  {fileType === 'pdf' ? 'Extracted Transactions Preview' : 'Sample Data Preview'}
                </Label>
                <div className="overflow-x-auto border rounded">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {headers.map(h => (
                          <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sampleRows.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-t">
                          {headers.map(h => (
                            <td key={h} className="px-3 py-2 truncate max-w-[200px]">{row[h]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={resetImport}>
                Start Over
              </Button>
              <Button 
                onClick={handleParseTransactions} 
                disabled={loading || (fileType !== 'pdf' && (!mapping.date_column || !mapping.description_column))}
                className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
              >
                {loading ? 'Processing...' : (fileType === 'pdf' ? 'Continue to Review' : 'Parse Transactions')}
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Check size={20} />
                Review Transactions
              </span>
              <Badge variant="outline">
                {selectedTxns.size} of {transactions.length} selected
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {errors.length > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-700 flex items-center gap-2">
                  <AlertTriangle size={16} />
                  {errors.length} rows had errors and were skipped
                </p>
              </div>
            )}

            <div className="mb-4 flex items-center gap-2">
              <Checkbox checked={selectedTxns.size === transactions.length} onCheckedChange={toggleAll} />
              <Label>Select All</Label>
            </div>

            <div className="max-h-96 overflow-y-auto border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="w-10 px-3 py-2"></th>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Description</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-left">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn, i) => (
                    <tr key={i} className={`border-t ${selectedTxns.has(i) ? '' : 'opacity-50'}`}>
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={selectedTxns.has(i)}
                          onCheckedChange={() => toggleTransaction(i)}
                        />
                      </td>
                      <td className="px-3 py-2">{txn.date}</td>
                      <td className="px-3 py-2 max-w-[200px] truncate">{txn.description}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={txn.transaction_type === 'revenue' ? 'text-green-600' : 'text-red-600'}>
                          {txn.transaction_type}
                        </Badge>
                      </td>
                      <td className={`px-3 py-2 text-right font-medium ${txn.transaction_type === 'revenue' ? 'text-green-600' : 'text-red-600'}`}>
                        ${txn.amount.toFixed(2)}
                      </td>
                      <td className="px-3 py-2">
                        {txn.suggested_category_name ? (
                          <span className="flex items-center gap-1">
                            <Brain size={14} className="text-[hsl(var(--brand-500))]" />
                            {txn.suggested_category_name}
                            <span className="text-xs text-gray-400">({Math.round(txn.confidence * 100)}%)</span>
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                Back to Mapping
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={loading || selectedTxns.size === 0}
                className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
              >
                {loading ? 'Importing...' : `Import ${selectedTxns.size} Transactions`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Complete */}
      {step === 4 && (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Import Complete!</h2>
            <p className="text-[hsl(var(--muted-ink))] mb-6">
              Your transactions have been imported successfully.
            </p>
            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={resetImport}>
                <RefreshCw size={16} className="mr-2" />
                Import More
              </Button>
              <Button 
                onClick={() => window.location.href = '/transactions'}
                className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
              >
                View Transactions
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BankImport;
